import auth from "@/helpers/auth";
import axios, { getApiErrorCode } from "@/plugins/axios";
import logger, {
  createAttemptId,
  getErrorDiagnostic,
  logAuthEvent,
  syncAppsLogsClientContext,
  toLoggerUser,
} from "@/plugins/appsLogsCore";

/**
 * Initial state
 */
export const state = {
  user: {
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    locale: "",
  },
  notifications: {},
  token: auth.getToken(),
  valid: false,
  checked: false,
  pushable: localStorage.getItem("pushnot_subscription"),
};

/**
 * Mutations
 */
export const mutations = {
  ["SET_USER"](state, data) {
    state.valid = true;
    const userData = { ...data };
    state.notifications = userData.notifications || state.notifications || {};
    delete userData.notifications;
    // Preferences are handled by the preferences module – don't store them on
    // the user object, but do make them available for the action to merge.
    delete userData.preferences;
    state.user = userData;
    if (userData.access_token || userData.token || userData.refresh_token) {
      const token = auth.saveAuthResponse(userData);
      state.token = token || auth.getAccessToken();
    } else {
      state.token = auth.getAccessToken();
    }
  },
  ["EDIT_WHATSAPP"](state, data) {
    state.valid = true;
    state.notifications.whatsapp = data;
    // delete data.notifications;
    // state.user = data;
    // if (data.token) {
    //   auth.save(data.token);
    //   state.token = data.token;
    // } else {
    //   state.token = auth.getToken();
    // }
  },
  ["EDIT_MAIL"](state, data) {
    state.valid = true;
    state.notifications.email = data;
  },
  ["OUT_USER"](state) {
    state.user = {
      email: "",
      first_name: "",
      last_name: "",
      phone: "",
      locale: "",
      authorization: {
        permissions: {},
        role_permissions: [],
      },
    };
    state.notifications = {};
    state.token = "";
    state.valid = false;
    this.dispatch("devices/logOut");
    this.dispatch("groups/logOut");
    this.dispatch("general/logOut");
    this.dispatch("business/logOut");
    // Avoid forcing a reload for auth state changes; router/app state can react.
  },
  ["EDIT_USER"](state, data) {
    // console.log(data);
    if ("firs_name" in data) {
      state.user.first_name = data.first_name;
      state.user.last_name = data.last_name;
    } else if ("password" in data) {
      state.user.password = data.password;
    } else if ("email" in data) {
      state.user.email = data.email;
    } else if ("phone" in data) {
      state.user.phone = data.phone;
    }
  },
  ["SET_PUSHNOTS"](state, value) {
    state.pushable = value;
  },
  ["SET_CHECKED"](state) {
    // setTimeout(() => {
    state.checked = true;
    //   if (state.checked) state.checked = true;
    // }, 1000);
  },
  ["SET_LOCALE"](state, value) {
    state.user.locale = value;
  },
  // ["CLEAR_NOTS"](state) {
  //   state.user.notifications_used = 0;
  // },
};

/**
 * Actions
 */
export const actions = {
  setPushnots() {
    localStorage.removeItem("deniedPush");
  },
  async get({ commit }) {
    let result = {
      ok: false,
      authFailed: false,
      temporaryFailure: false,
      error: null,
    };

    await axios
      .get("user")
      .then((res) => {
        // console.log("user/signin", res.data);
        if (res.data.data) {
          const data = res.data.data;
          // Merge server-side preferences before committing the user so that
          // the router landing-page redirect uses the up-to-date preference.
          if (data.preferences && typeof data.preferences === "object") {
            try {
              this.dispatch("preferences/mergeFromServer", data.preferences);
            } catch (_) {
              // preferences module not available – ignore
            }
          }
          commit("SET_USER", data);
          logger.identify(toLoggerUser(data));
          const clientContext = syncAppsLogsClientContext();
          logger.auth("auth.user_restored", clientContext);
          result.ok = true;
        }
      })
      .catch((err) => {
        console.log("user/get", err);
        logger.auth("auth.user_restore_failed", {
          authFailed: !!err.authFailed,
          status: err.response && err.response.status,
        });
        result.error = err;
        result.authFailed = !!err.authFailed;
        result.temporaryFailure = !err.authFailed;
      })
      .finally(() => {
        commit("SET_CHECKED");
      });

    return result;
  },
  async signIn({ commit }, user) {
    const result = {
      ok: false,
      errorType: null,
      errorKey: null,
      status: null,
      code: null,
      retryAfterSeconds: null,
    };
    const payload = {
      ...user,
      ...auth.getDeviceMetadata(),
    };

    syncAppsLogsClientContext();

    await axios
      .post("user/signin", payload)
      .then((res) => {
        // console.log("user/signin", res.data);
        if (res.data) {
          const data = res.data.data || res.data;
          const token = auth.saveAuthResponse(data);
          axios.defaults.headers.Authorization =
            "Bearer " + (token || auth.getAccessToken());

          // Merge server-side preferences so the router landing-page redirect
          // uses the up-to-date preference immediately after sign-in.
          if (data.preferences && typeof data.preferences === "object") {
            try {
              this.dispatch("preferences/mergeFromServer", data.preferences);
            } catch (_) {
              // preferences module not available – ignore
            }
          }
          commit("SET_USER", data);
          logger.identify(toLoggerUser(data));
          const clientContext = syncAppsLogsClientContext();
          logger.auth("auth.login_succeeded", {
            method: "password",
            ...clientContext,
          });
          this.dispatch("general/setAlert", {
            type: "success",
            msg: "signIn",
          });
          commit("SET_CHECKED");
          result.ok = true;
        }
      })
      .catch((err) => {
        const status = err.response && err.response.status;
        const code = getApiErrorCode(err);
        const headers = (err.response && err.response.headers) || {};
        const retryHeaderRaw =
          headers["retry-after"] ||
          headers["Retry-After"] ||
          headers["retry_after"];
        const retryFromHeader = Number.parseInt(
          String(retryHeaderRaw || "")
            .replace(":", "")
            .trim(),
          10
        );
        const retryFromBody = Number.parseInt(
          String(
            (err.response &&
              err.response.data &&
              err.response.data.retry_after) ||
              ""
          ),
          10
        );
        result.status = status || null;
        result.code = code || null;
        result.retryAfterSeconds =
          Number.isFinite(retryFromHeader) && retryFromHeader > 0
            ? retryFromHeader
            : Number.isFinite(retryFromBody) && retryFromBody > 0
            ? retryFromBody
            : null;

        const isTooManyAttempts = status === 429 || code === "RATE_LIMITED";
        const isInvalidCredentials =
          status === 400 ||
          status === 401 ||
          status === 422 ||
          code === "UNAUTHORIZED";

        if (isTooManyAttempts) {
          result.errorType = "TOO_MANY_ATTEMPTS";
          result.errorKey = "login-error-too-many-attempts";
          if (!result.retryAfterSeconds) {
            result.retryAfterSeconds = 60;
          }
        } else if (isInvalidCredentials) {
          result.errorType = "INVALID_CREDENTIALS";
          result.errorKey = "login-error-invalid-credentials";
        } else {
          result.errorType = "SERVER_ERROR";
          result.errorKey = "login-error-server";
        }

        logger.auth("auth.login_failed", {
          method: "password",
          clientType: auth.getClientType(),
          deviceId: auth.getDeviceId(),
          status,
          code,
          errorType: result.errorType,
        });
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: result.errorKey || "oopsUser",
        });
      });

    return result;
  },
  async signOut({ state }, options = {}) {
    const startedAt = Date.now();
    const logoutAttemptId =
      options.logoutAttemptId || createAttemptId("logout");
    const reason = options.reason || "user_requested";
    const source = options.source || "unknown";
    const actor = toLoggerUser(state.user);
    const refreshToken = auth.getRefreshToken();
    let apiAttempted = false;
    let apiSucceeded = false;
    let apiDurationMs = 0;

    logAuthEvent("auth.logout_started", {
      logoutAttemptId,
      reason,
      source,
      hasRefreshToken: !!refreshToken,
      actor,
    });

    if (refreshToken) {
      apiAttempted = true;
      const apiStartedAt = Date.now();
      logAuthEvent("auth.logout_api_started", {
        logoutAttemptId,
        reason,
        source,
      });

      try {
        axios
          .post("user/logout", {
            refresh_token: refreshToken,
          })
          .then((response) => {
            apiSucceeded = true;
            apiDurationMs = Date.now() - apiStartedAt;
            logAuthEvent("auth.logout_api_succeeded", {
              logoutAttemptId,
              reason,
              source,
              status: response.status,
              durationMs: apiDurationMs,
            });
          })
          .catch((err) => {
            apiDurationMs = Date.now() - apiStartedAt;
            logAuthEvent(
              "auth.logout_api_failed",
              {
                logoutAttemptId,
                reason,
                source,
                durationMs: apiDurationMs,
                ...getErrorDiagnostic(err),
              },
              { level: "warn" }
            );
            console.log("user/logout", err);
          });
      } catch (err) {
        logAuthEvent(
          "auth.logout_api_failed_to_start",
          {
            logoutAttemptId,
            reason,
            source,
            ...getErrorDiagnostic(err),
          },
          { level: "warn" }
        );
      }
    } else {
      logAuthEvent("auth.logout_api_skipped_no_refresh", {
        logoutAttemptId,
        reason,
        source,
      });
    }

    logAuthEvent("auth.logout_completed", {
      logoutAttemptId,
      reason,
      source,
      apiAttempted,
      apiSucceeded,
      apiDurationMs,
      durationMs: Date.now() - startedAt,
      actor,
    });

    return true;
  },
  clearSessionState({ commit }) {
    logger.auth("auth.clear_session_state");
    commit("OUT_USER");
  },
  clearClientSessionData(_, options = {}) {
    const reason = options.reason || "logout_called";

    logger.auth("auth.logout_local_cleanup_started", {
      reason,
    });

    auth.logout(reason);

    // Clear unified preferences from localStorage on logout
    try {
      this.dispatch("preferences/logOut");
    } catch (_) {
      // preferences module may not be available in all contexts
    }

    const cleanupKeys = [
      "mydata",
      "mydataview",
      "has-groups",
      "inGroup",
      "getContact",
      "first_login",
      "pushnot_subscription",
      // Legacy preference keys (migrated into switchq:userPreferences)
      "dark",
      "switchq:viewPreferences",
      "groups_view_mode",
      "homePage",
    ];
    cleanupKeys.forEach((key) => localStorage.removeItem(key));

    try {
      sessionStorage.clear();
    } catch (_) {
      // ignore storage access issues
    }

    delete axios.defaults.headers.Authorization;

    logger.auth("auth.logout_local_cleanup_completed", {
      reason,
      cleanupKeysRemoved: cleanupKeys.length,
    });
  },
  async socketUser({ commit }, data) {
    commit("SET_USER", data);
  },
  async editUser({ commit }, data) {
    let valid = false;
    // console.log("data to edit ", data);
    await axios
      .post("/user/edit", data)
      .then(() => {
        commit("EDIT_USER", data);
        if ("first_name" in data) {
          this.dispatch("general/setAlert", {
            type: "success",
            msg: "nameChange",
          });
        } else if ("password" in data) {
          this.dispatch("general/setAlert", {
            type: "success",
            msg: "passChange",
          });
        } else if ("email" in data) {
          this.dispatch("general/setAlert", {
            type: "success",
            msg: "emailChange",
          });
        } else if ("phone" in data) {
          this.dispatch("general/setAlert", {
            type: "success",
            msg: "phoneChange",
          });
        }
        valid = true;
      })
      .catch((err) => {
        if (err.response.data.errors.email) {
          this.dispatch("general/setAlert", {
            type: "failed",
            msg: "emailTaken",
          });
        } else {
          this.dispatch("general/setAlert", {
            type: "failed",
            msg: "oops",
          });
        }
      });
    return valid;
  },
  async editNotifications({ commit }, request) {
    await axios
      .post("user/notifications/edit", request)
      .then((res) => {
        commit("SET_USER", res.data.data);
      })
      .catch(() => {
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
  },
  async cancelEmailNotifications({ commit }, request) {
    await axios
      .get("user/notifications/mail/unsubscribe", request)
      .then(() => {
        commit("EDIT_MAIL", 0);
      })
      .catch(() => {
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
  },
  async cancelWhatsAppNotifications({ commit }, request) {
    await axios
      .get("user/notifications/whatsapp/unsubscribe", request)
      .then(() => {
        commit("EDIT_WHATSAPP", 0);
      })
      .catch(() => {
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
  },
  async toggleEmail({ commit }) {
    commit("EDIT_MAIL", 1);
  },
  // async clearNotsCount({ commit }) {
  //   commit("CLEAR_NOTS");
  // },
  async toggleEmailFalse({ commit }) {
    commit("EDIT_MAIL", 0);
  },
  async toggleWhatsApp({ commit }) {
    commit("EDIT_WHATSAPP", 1);
  },
  // async clearNotsCount({ commit }) {
  //   commit("CLEAR_NOTS");
  // },
  async toggleWhatsAppFalse({ commit }) {
    commit("EDIT_WHATSAPP", 0);
  },
  async editEmailNotifications({ commit }, request) {
    let valid = {
      valid: true,
      subscribe: false,
    };
    await axios
      .post("user/notifications/mail/subscribe", request)
      .then(async (res) => {
        if (res.data.data && res.status == 200) {
          valid.valid = true;
          valid.subscribe = true;
          commit("EDIT_MAIL", 1);
        }
        if (res.data.data && res.status == 201) {
          valid.valid = true;
          valid.subscribe = "code sent!";
          commit("EDIT_MAIL", 0);
        }
      })
      .catch(() => {
        valid.valid = false;
        valid.subscribe = false;
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
    return valid;
  },
  async editWhatsAppNotifications({ commit }, request) {
    let valid = {
      valid: true,
      subscribe: false,
    };
    await axios
      .post("user/notifications/whatsapp/subscribe", request)
      .then(async (res) => {
        if (res.data.data && res.status == 200) {
          valid.valid = true;
          valid.subscribe = true;
          commit("EDIT_WHATSAPP", 1);
        }
        if (res.data.data && res.status == 201) {
          valid.valid = true;
          valid.subscribe = "code sent!";
          commit("EDIT_WHATSAPP", 0);
        }
      })
      .catch(() => {
        valid.valid = false;
        valid.subscribe = false;
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
    return valid;
  },
  async getWhatsAppSubscribtion({ commit }) {
    let valid = {
      valid: true,
      subscribe: false,
    };
    await axios
      .get("user/notifications/whatsapp")
      .then(async (res) => {
        // console.log("res", res.data);
        if (res.data.data) {
          valid.valid = true;
          valid.subscribe = true;
          commit("EDIT_WHATSAPP", 1);
        }
        if (!res.data.data) {
          valid.valid = true;
          valid.subscribe = false;
          commit("EDIT_WHATSAPP", 0);
        }
      })
      .catch(() => {
        valid.valid = false;
        valid.subscribe = false;
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
    return valid;
  },
  async getEmailSubscribtion({ commit }) {
    let valid = {
      valid: true,
      subscribe: false,
    };
    await axios
      .get("user/notifications/mail")
      .then(async (res) => {
        // console.log("res", res.data);
        if (res.data.data) {
          valid.valid = true;
          valid.subscribe = true;
          commit("EDIT_MAIL", 1);
        }
        if (!res.data.data) {
          valid.valid = true;
          valid.subscribe = false;
          commit("EDIT_MAIL", 0);
        }
      })
      .catch(() => {
        valid.valid = false;
        valid.subscribe = false;
        this.dispatch("general/setAlert", {
          type: "failed",
          msg: "oops",
        });
      });
    return valid;
  },
  async setLocale({ commit }, value) {
    await axios
      .get("/user/locale?locale=" + value)
      .then(async () => {
        await commit("SET_LOCALE", value);
      })
      .catch((err) => console.log(err));
  },
};

/**
 * Getters
 */
export const getters = {
  token: (state) => state.token,
  valid: (state) => state.valid,
  user: (state) => state.user,
  isPermitted: (state) => (slug) => {
    // console.log("slug", slug);
    // console.log("state.user", state.user);
    if (
      !state.user.authorization ||
      !state.user.authorization.permissions ||
      !state.user.authorization.role_permissions
    ) {
      return false;
    }

    return (
      (slug in state.user.authorization.permissions &&
        state.user.authorization.permissions[slug] != 0) ||
      (state.user.authorization.role_permissions.includes(slug) &&
        !(slug in state.user.authorization.permissions))
    );
  },
  checked: (state) => state.checked,
  name: (state) => state.user.first_name + " " + state.user.last_name,
  notifications: (state) => state.notifications,
};
